/**
 * AwsAccessRequestField — compound scaffolder field extension.
 *
 * Manages all AWS SSO access request UI state internally and returns a
 * single structured object. This avoids RJSF stale-state issues and
 * eliminates the need for complex allOf/if/then schema conditionals.
 *
 * Template usage:
 *
 *   accessRequest:
 *     title: AWS Access
 *     type: object
 *     ui:field: AwsAccessRequestField
 */

import { useCallback, useEffect, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi, fetchApiRef, configApiRef } from '@backstage/core-plugin-api';
import { JsonObject } from '@backstage/types';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ACCOUNT_TYPES = [
  { value: 'nonprod', label: 'Non-Production' },
  { value: 'prod', label: 'Production' },
  { value: 'innovation', label: 'Innovation' },
];

const ROLES = [
  {
    value: 'reader',
    label: 'reader — permanent limited read-only access (excludes Secrets Manager)',
  },
  {
    value: 'reader-service',
    label: 'reader-service — service-specific temporary read-only access (e.g. DynamoDB)',
  },
  {
    value: 'writer-ops',
    label: 'writer-ops — temporary elevated write access (only when CI/CD is not feasible)',
  },
  {
    value: 'critical-ops',
    label: 'critical-ops — temporary elevated access for SRE & DevOps to address P1 issues',
  },
];

const SERVICES = [
  'ACM', 'API Gateway', 'AppStream', 'Backup', 'CloudFormation', 'CloudFront',
  'CloudWatch', 'DMS', 'DNS Manager', 'DynamoDB', 'EC2', 'ECR', 'ECS', 'EFS',
  'ElastiCache', 'EventBridge', 'Elastic Load Balancing', 'Glue', 'KMS', 'Lambda', 'Pinpoint', 'QuickSight', 'RDS',
  'reader-cybersecurity', 'Redshift',
  'Rotate IAM SES User (create new SMTP credentials)',
  'Rotate IAM User', 'Route 53', 'S3', 'Secrets Manager', 'Security Hub', 'Service Discovery',
  'SES', 'SNS', 'SQS', 'Step functions', 'Systems Manager', 'WAF', 'X-Ray',
];

const ROLES_NEEDING_SERVICES = new Set(['reader-service', 'writer-ops']);
const ROLES_NEEDING_DURATION = new Set(['reader-service', 'writer-ops', 'critical-ops']);
const ROLES_NEEDING_JIRA = new Set(['writer-ops', 'critical-ops']);

const JIRA_URL_PREFIX = 'https://advancedcsg.atlassian.net/';
const SERVICENOW_URL_PREFIX = 'https://oneadvanced.service-now.com/';
// Matches: https://advancedcsg.atlassian.net/browse/PROJ-123
//      OR: https://oneadvanced.service-now.com/...
const JIRA_URL_REGEX = /^(https:\/\/advancedcsg\.atlassian\.net\/browse\/[A-Z][A-Z0-9]+-\d+|https:\/\/oneadvanced\.service-now\.com\/.+)$/

/**
 * Submit-time validation for the AwsAccessRequestField scaffolder extension.
 * Exported so App.tsx can pass it directly to createScaffolderFieldExtension,
 * keeping all business rules in one place.
 */
export function validateAwsAccessRequest(
  value: unknown,
  validation: { addError: (msg: string) => void },
) {
  if (!value || typeof value !== 'object') return;
  const v = value as Record<string, unknown>;
  const isNonInnovation = v.accountType === 'nonprod' || v.accountType === 'prod';
  const needsJira =
    isNonInnovation &&
    Boolean(v.accountId) &&
    ROLES_NEEDING_JIRA.has(String(v.role ?? ''));
  if (!needsJira) return;
  const jira = String(v.jiraTicket ?? '');
  if (!jira) {
    validation.addError('A JIRA or ServiceNow ticket URL is required for this access level');
  } else if (!JIRA_URL_REGEX.test(jira)) {
    validation.addError(
      `Must be a valid JIRA URL (${JIRA_URL_PREFIX}browse/PROJ-123) or ServiceNow URL (beginning with ${SERVICENOW_URL_PREFIX}...)`,
    );
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SelectItem {
  value: string;
  label: string;
}

interface AccessRequestState {
  accountType: string;
  accountId: string;
  role: string;
  services: string[];
  duration: number;
  jiraTicket: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toSelectItems(data: unknown): SelectItem[] {
  let arr: unknown[] = [];
  if (Array.isArray(data)) {
    arr = data;
  } else if (typeof data === 'object' && data !== null) {
    // Try common wrapper keys
    const obj = data as Record<string, unknown>;
    const wrapped = obj.accounts ?? obj.items ?? obj.data;
    if (Array.isArray(wrapped)) arr = wrapped;
  }
  return arr.map(item => {
    if (typeof item === 'string') return { value: item, label: item };
    if (typeof item === 'object' && item !== null) {
      const o = item as Record<string, unknown>;
      const value = String(o.id ?? o.value ?? JSON.stringify(o));
      const label = String(o.name ?? o.label ?? value);
      return { value, label };
    }
    return { value: String(item), label: String(item) };
  });
}

function toJsonObject(state: AccessRequestState): JsonObject {
  const base: JsonObject = {
    accountType: state.accountType,
    accountId: state.accountId,
    role: state.role || null,
    services: state.services,
    duration: state.duration,
    jiraTicket: state.jiraTicket,
  };
  return base;
}

const EMPTY_STATE: AccessRequestState = {
  accountType: '',
  accountId: '',
  role: '',
  services: [],
  duration: 60,
  jiraTicket: '',
};

function fromFormData(formData: JsonObject | undefined): AccessRequestState {
  if (!formData) return { ...EMPTY_STATE };
  return {
    accountType: String(formData.accountType ?? ''),
    accountId: String(formData.accountId ?? ''),
    role: String(formData.role ?? ''),
    services: Array.isArray(formData.services)
      ? (formData.services as string[])
      : [],
    duration: typeof formData.duration === 'number' ? formData.duration : 60,
    jiraTicket: String(formData.jiraTicket ?? ''),
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AwsAccessRequestField = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<JsonObject>) => {
  const fetchApi = useApi(fetchApiRef);
  const configApi = useApi(configApiRef);
  const backendBaseUrl = configApi.getString('backend.baseUrl');

  const [state, setState] = useState<AccessRequestState>(() =>
    fromFormData(formData),
  );
  const [accounts, setAccounts] = useState<SelectItem[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState<string>();
  const [jiraTicketTouched, setJiraTicketTouched] = useState(false);

  // Emit the full structured object whenever state changes.
  useEffect(() => {
    if (state.accountType) {
      onChange(toJsonObject(state));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // Fetch accounts whenever a concrete accountType is selected.
  useEffect(() => {
    if (!state.accountType || state.accountType === 'innovation') {
      setAccounts([]);
      return () => {};
    }

    let cancelled = false;
    const url = `${backendBaseUrl}/api/proxy/aws-sso-accounts-list/dev/accounts?accountType=${state.accountType}`;

    setAccountsLoading(true);
    setAccountsError(undefined);

    fetchApi
      .fetch(url)
      .then(async res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (!cancelled) {
          setAccounts(toSelectItems(data));
          setAccountsLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setAccountsError(String(err.message ?? err));
          setAccountsLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [state.accountType, backendBaseUrl, fetchApi]);

  // Handlers — each clears downstream fields when a parent value changes.
  const handleAccountType = useCallback((value: string) => {
    setState({
      ...EMPTY_STATE,
      accountType: value,
    });
  }, []);

  const handleAccountId = useCallback((value: string) => {
    setState(prev => ({
      ...prev,
      accountId: value,
      role: '',
      services: [],
      duration: 60,
      jiraTicket: '',
    }));
  }, []);

  const handleRole = useCallback((value: string) => {
    setState(prev => ({
      ...prev,
      role: value,
      services: [],
      duration: 60,
      jiraTicket: '',
    }));
  }, []);

  const handleServiceToggle = useCallback((service: string) => {
    setState(prev => {
      const next = prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service];
      return { ...prev, services: next };
    });
  }, []);

  const handleDuration = useCallback((_: unknown, value: number | number[]) => {
    setState(prev => ({ ...prev, duration: Array.isArray(value) ? value[0] : value }));
  }, []);

  const handleJiraTicket = useCallback((value: string) => {
    setState(prev => ({ ...prev, jiraTicket: value }));
  }, []);

  // Reset jira touched state when the role or account changes.
  useEffect(() => {
    setJiraTicketTouched(false);
  }, [state.role, state.accountId]);

  // When rawErrors clears (user fixed the value and re-validated), also clear
  // the touched state so the inline error doesn't flash back in.
  useEffect(() => {
    if (!rawErrors?.length) setJiraTicketTouched(false);
  }, [rawErrors]);

  // Derived flags.
  const isNonInnovation =
    state.accountType === 'nonprod' || state.accountType === 'prod';
  const showAccount = isNonInnovation;
  const showRole = isNonInnovation && Boolean(state.accountId);
  const showServices = showRole && ROLES_NEEDING_SERVICES.has(state.role);
  const showDuration = showRole && ROLES_NEEDING_DURATION.has(state.role);
  const showJira = showRole && ROLES_NEEDING_JIRA.has(state.role);

  let jiraTicketError = '';
  if (showJira && jiraTicketTouched) {
    if (!state.jiraTicket) {
      jiraTicketError = 'A JIRA or ServiceNow ticket URL is required';
    } else if (!JIRA_URL_REGEX.test(state.jiraTicket)) {
      jiraTicketError = `Must be a valid JIRA URL (${JIRA_URL_PREFIX}browse/PROJ-123) or ServiceNow URL (beginning with ${SERVICENOW_URL_PREFIX}...)`;
    }
  }

  // General (non-Jira) errors surface against Account Type.
  const rawGeneralErrors = showJira ? [] : (rawErrors ?? []);
  const hasGeneralError = rawGeneralErrors.length > 0;

  // Once rawErrors is present (post-Review), the framework renders them below
  // the field itself — suppress our touched-state error to avoid a duplicate.
  const activeJiraError = rawErrors?.length ? '' : jiraTicketError;

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 24 }}>
      {/* Account Type */}
      <FormControl fullWidth required={required} error={hasGeneralError} variant="outlined">
        <InputLabel shrink>Account Type</InputLabel>
        <Select
          value={state.accountType}
          label="Account Type"
          displayEmpty
          onChange={e => handleAccountType(e.target.value as string)}
        >
          <MenuItem disabled value="">
            <em>Select account type</em>
          </MenuItem>
          {ACCOUNT_TYPES.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
        {rawGeneralErrors.map((e, i) => (
          <FormHelperText key={i}>{e}</FormHelperText>
        ))}
      </FormControl>

      {/* AWS Account (nonprod / prod only) */}
      {showAccount && (
        <FormControl fullWidth required error={Boolean(accountsError)} variant="outlined">
          <InputLabel shrink>AWS Account</InputLabel>
          <Select
            value={state.accountId}
            label="AWS Account"
            displayEmpty
            disabled={accountsLoading || Boolean(accountsError)}
            onChange={e => handleAccountId(e.target.value as string)}
          >
            {accountsLoading ? (
              <MenuItem disabled value="">
                <CircularProgress size={16} style={{ marginRight: 8 }} />
                Loading accounts…
              </MenuItem>
            ) : (
              <MenuItem disabled value="">
                <em>Choose an AWS Account</em>
              </MenuItem>
            )}
            {accounts.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          {accountsError && (
            <FormHelperText>
              Failed to load accounts: {accountsError}. Refresh the page to try again.
            </FormHelperText>
          )}
        </FormControl>
      )}

      {/* SSO Role */}
      {showRole && (
        <FormControl fullWidth required variant="outlined">
          <InputLabel shrink>AWS SSO Role</InputLabel>
          <Select
            value={state.role}
            label="AWS SSO Role"
            displayEmpty
            onChange={e => handleRole(e.target.value as string)}
          >
            <MenuItem disabled value="">
              <em>Select access level</em>
            </MenuItem>
            {ROLES.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Services (reader-service / writer-ops) */}
      {showServices && (
        <FormControl component="fieldset" required>
          <FormLabel component="legend">Services</FormLabel>
          <FormGroup>
            {SERVICES.map(service => (
              <FormControlLabel
                key={service}
                control={
                  <Checkbox
                    checked={state.services.includes(service)}
                    onChange={() => handleServiceToggle(service)}
                    size="small"
                  />
                }
                label={service}
              />
            ))}
          </FormGroup>
          <FormHelperText>
            Select the AWS services you need access to
          </FormHelperText>
        </FormControl>
      )}

      {/* Duration (temporary roles) */}
      {showDuration && (
        <FormControl fullWidth>
          <Typography gutterBottom>
            Duration (minutes): <strong>{state.duration}</strong>
          </Typography>
          <Slider
            value={state.duration}
            min={1}
            max={240}
            step={1}
            onChange={handleDuration}
            valueLabelDisplay="auto"
          />
          <FormHelperText>
            How long access is needed. Defaults to 60 minutes.
          </FormHelperText>
        </FormControl>
      )}

      {/* JIRA / ServiceNow Ticket (writer-ops / critical-ops) */}
      {showJira && (
        <TextField
          fullWidth
          required
          label="JIRA / ServiceNow Ticket URL"
          variant="outlined"
          value={state.jiraTicket}
          onChange={e => handleJiraTicket(e.target.value)}
          onBlur={() => setJiraTicketTouched(true)}
          error={Boolean(activeJiraError || rawErrors?.length)}
          helperText={
            activeJiraError ||
            (rawErrors?.length
              ? '' // framework renders its own error below; keep height stable
              : `The JIRA or ServiceNow ticket that tracks this work — required for audit and compliance purposes. e.g. ${JIRA_URL_PREFIX}browse/PROJ-123 or beginning with ${SERVICENOW_URL_PREFIX}...`)
          }
          InputLabelProps={{ shrink: true }}
        />
      )}
    </Box>
  );
};
